import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membersAPI, membershipApplicationAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { ArrowLeft, FileText, User, Calendar, File, Phone, Mail } from 'lucide-react';
import { toast } from 'react-toastify';

const RAW_API_URL = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/+$/, '');
const API_ORIGIN = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;
const API_BASE_URL = API_ORIGIN;
const MEDIA_ENDPOINT = `${API_ORIGIN}/api/media`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem 1.5rem;
  margin-top: 0.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const MetaLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MetaValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const FieldItem = styled.div`
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
`;

const FieldLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 0.25rem;
`;

const FieldValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-line;
  word-break: break-word;
`;

const FormRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const FormRow = styled.div`
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const FormRowLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 0.15rem;
`;

const FormRowValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const EmptyState = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;
`;

const StatusBadge = styled.span`
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${({ status }) => {
    switch (status) {
      case 'submitted':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'under_review':
        return 'background: #fff3e0; color: #f57c00;';
      case 'committee_review':
        return 'background: #fce4ec; color: #c2185b;';
      case 'eligible':
        return 'background: #f3e5f5; color: #7b1fa2;';
      case 'resubmit':
        return 'background: #fff8e1; color: #f9a825;';
      case 'approved':
        return 'background: #e8f5e9; color: #388e3c;';
      case 'rejected':
        return 'background: #ffebee; color: #d32f2f;';
      default:
        return 'background: #f5f5f5; color: #666;';
    }
  }}
`;

const resolveFileUrl = (input) => {
  if (!input) return null;
  const path = String(input);

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const normalize = (value) => {
    let normalized = value.replace(/\\/g, '/');

    if (normalized.startsWith('/storage/')) {
      normalized = normalized.replace(/^\/storage\//, '');
    } else if (normalized.startsWith('storage/')) {
      normalized = normalized.replace(/^storage\//, '');
    }

    if (normalized.startsWith('public/')) {
      normalized = normalized.replace(/^public\//, '');
    }

    return normalized;
  };

  if (path.startsWith('/')) {
    if (path.startsWith('/storage/')) {
      const cleaned = normalize(path);
      return `${MEDIA_ENDPOINT}?path=${encodeURIComponent(cleaned)}`;
    }

    return `${API_BASE_URL}${path}`;
  }

  const cleaned = normalize(path);
  return `${MEDIA_ENDPOINT}?path=${encodeURIComponent(cleaned)}`;
};

const MemberApplicationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);

  const registrationFormData = submission?.form_data || {};
  const registrationFiles = useMemo(() => {
    if (!submission?.files) return {};
    return Object.entries(submission.files).reduce((acc, [key, value]) => {
      if (!value) return acc;
      acc[key] = resolveFileUrl(value);
      return acc;
    }, {});
  }, [submission]);

  const {
    accessibility,
    supportingDocs,
  } = useMemo(() => {
    const formData = submission?.form_data || {};
    const files = registrationFiles || {};

    const normalizeBoolean = (value) => {
      if (typeof value === 'boolean') return value;
      if (value === undefined || value === null) return null;
      const normalized = String(value).toLowerCase();
      if (['1', 'yes', 'true', 'y'].includes(normalized)) return true;
      if (['0', 'no', 'false', 'n'].includes(normalized)) return false;
      return null;
    };

    const disabilityStatus = normalizeBoolean(
      formData.has_disability ??
      formData.physical_disability ??
      formData.disability_status
    );

    let disabilityTypes = [];
    const typesValue = formData.disability_types ?? formData.disability_type ?? [];
    if (Array.isArray(typesValue)) {
      disabilityTypes = typesValue.filter(Boolean);
    } else if (typeof typesValue === 'string' && typesValue.trim().length) {
      disabilityTypes = typesValue.split(',').map(item => item.trim()).filter(Boolean);
    }

    const disabilityOther =
      formData.disability_other ??
      formData.disability_details ??
      '';

    const cvPath =
      files.cv ||
      formData.cv ||
      formData.resume ||
      formData.cv_url ||
      null;

    const cvUrl = cvPath ? resolveFileUrl(cvPath) : null;

    return {
      accessibility: {
        disabilityStatus,
        disabilityTypes,
        disabilityOther: typeof disabilityOther === 'string' ? disabilityOther.trim() : '',
      },
      supportingDocs: {
        cvUrl,
      },
    };
  }, [submission, registrationFiles]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await membersAPI.getMember(id);
        const data = res.data || res.data?.data || res;
        setMember(data);
        if (data.latest_submission) {
          setSubmission(data.latest_submission);
        } else {
          setSubmission(null);
        }
      } catch (e) {
        console.error('Failed to load member or submission', e);
        toast.error('Failed to load member application');
        navigate('/app/members');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  useEffect(() => {
    const loadForm = async () => {
      if (!submission?.form_id) {
        setFormConfig(null);
        return;
      }
      try {
        setLoadingForm(true);
        const res = await membershipApplicationAPI.getForm(submission.form_id);
        const form = res.data || res.data?.data || res;
        setFormConfig(form);
      } catch (e) {
        console.error('Failed to load application form', e);
        setFormConfig(null);
      } finally {
        setLoadingForm(false);
      }
    };

    loadForm();
  }, [submission?.form_id]);

  const applicationFields = useMemo(() => {
    if (!formConfig) return [];
    const raw =
      formConfig.activeFields ||
      formConfig.active_fields ||
      formConfig.fields ||
      [];
    return raw
      .filter((f) => f && f.field_name)
      .slice()
      .sort((a, b) => {
        const aOrder = typeof a.display_order === 'number' ? a.display_order : 0;
        const bOrder = typeof b.display_order === 'number' ? b.display_order : 0;
        return aOrder - bOrder;
      });
  }, [formConfig]);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) {
      return value.map((v) => (typeof v === 'string' ? v.trim() : v)).filter(Boolean);
    }
    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map((v) => v.trim()).filter(Boolean);
    }
    return [];
  };

  const renderFieldValue = (field) => {
    const name = field.field_name;
    const type = field.field_type;
    const options = field.options || [];
    const rawValue = registrationFormData[name];

    let value = rawValue;

    // Skip file fields here (they are shown in the Supporting Documents section)
    if (type === 'file') {
      return <span style={{ color: theme.colors.text.secondary }}>Shown in documents section above</span>;
    }

    if (type === 'checkbox') {
      const arr = normalizeArray(value);
      if (!arr.length) {
        return <span style={{ color: theme.colors.text.secondary }}>No selection</span>;
      }
      return (
        <ChipsRow>
          {arr.map((v) => (
            <Chip key={v}>{v}</Chip>
          ))}
        </ChipsRow>
      );
    }

    if (type === 'radio' || type === 'select') {
      if (!value) {
        return <span style={{ color: theme.colors.text.secondary }}>Not selected</span>;
      }
      const matched = options.find((opt) => String(opt).toLowerCase() === String(value).toLowerCase());
      return <span>{matched || value}</span>;
    }

    if (type === 'textarea') {
      if (!value) {
        return <span style={{ color: theme.colors.text.secondary }}>Not provided</span>;
      }
      return <span>{String(value)}</span>;
    }

    if (value === undefined || value === null || value === '') {
      return <span style={{ color: theme.colors.text.secondary }}>Not provided</span>;
    }

    if (Array.isArray(value)) {
      const arr = normalizeArray(value);
      if (!arr.length) {
        return <span style={{ color: theme.colors.text.secondary }}>Not provided</span>;
      }
      return <span>{arr.join(', ')}</span>;
    }

    return <span>{String(value)}</span>;
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    if (status === 'resubmit') {
      return 'Needs Resubmission';
    }
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <PageContainer>
        <Card>
          <p>Loading application...</p>
      </Card>
    </PageContainer>
    );
  }

  if (!member) {
    return (
      <PageContainer>
        <Card>
          <p>Member not found.</p>
        </Card>
      </PageContainer>
    );
  }

  if (!submission) {
    return (
      <PageContainer>
        <Header>
          <Button variant="ghost" onClick={() => navigate(`/app/members/${id}`)} leftIcon={<ArrowLeft size={16} />}>
            Back to Member
          </Button>
          <TitleBlock>
            <Title>Application</Title>
            <Subtitle theme={theme}>This member does not have a recorded application submission.</Subtitle>
          </TitleBlock>
        </Header>
        <Card>
          <EmptyState theme={theme}>No application submission is associated with this member.</EmptyState>
        </Card>
      </PageContainer>
    );
  }

  const submittedAt = submission.submitted_at || submission.created_at;
  const interestedTrainings = submission.interested_trainings || [];
  const hasPreviousTraining = !!submission.has_previous_training;
  const previousTrainings = submission.previous_trainings || [];

  return (
    <PageContainer>
      <Header>
        <Button variant="ghost" onClick={() => navigate(`/app/members/${id}`)} leftIcon={<ArrowLeft size={16} />}>
          Back to Member
        </Button>
        <TitleBlock>
          <Title>Membership Application</Title>
          <Subtitle theme={theme}>
            {member.first_name || member.last_name
              ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
              : 'Member'}
          </Subtitle>
        </TitleBlock>
      </Header>

      <TwoColumn>
        <Card>
          <SectionTitle theme={theme}>
            <User size={18} />
            Applicant Overview
          </SectionTitle>
          <MetaRow>
            <MetaItem>
              <MetaLabel theme={theme}>Member ID</MetaLabel>
              <MetaValue theme={theme}>{member.member_id || 'Not assigned'}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel theme={theme}>Registration No.</MetaLabel>
              <MetaValue theme={theme}>{member.registration_no || 'Not provided'}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel theme={theme}>Status</MetaLabel>
              <MetaValue theme={theme}>
                {member.membership_status ? member.membership_status.replace('_', ' ') : 'Not provided'}
              </MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel theme={theme}>Approval Status</MetaLabel>
              <MetaValue theme={theme}>
                {member.approval_status ? member.approval_status.replace('_', ' ') : 'Not provided'}
              </MetaValue>
            </MetaItem>
          </MetaRow>

          <MetaRow style={{ marginTop: '1.5rem' }}>
            <MetaItem>
              <MetaLabel theme={theme}>Submitted On</MetaLabel>
              <MetaValue theme={theme}>
                {submittedAt ? new Date(submittedAt).toLocaleString() : 'N/A'}
              </MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel theme={theme}>Application Status</MetaLabel>
              <MetaValue theme={theme}>
                <StatusBadge status={submission.status}>{formatStatus(submission.status)}</StatusBadge>
              </MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel theme={theme}>Email</MetaLabel>
              <MetaValue theme={theme}>{member.email || 'Not provided'}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel theme={theme}>Phone</MetaLabel>
              <MetaValue theme={theme}>{member.phone || 'Not provided'}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel theme={theme}>Region / City</MetaLabel>
              <MetaValue theme={theme}>{member.region?.name || member.city || 'Not provided'}</MetaValue>
            </MetaItem>
          </MetaRow>
        </Card>

        <Card>
          <SectionTitle theme={theme}>
            <FileText size={18} />
            Supporting Documents
          </SectionTitle>
          <MetaRow>
            {submission.id_card_path && (
              <>
                <MetaItem>
                  <MetaLabel theme={theme}>ID Card</MetaLabel>
                  <MetaValue theme={theme}>
                    <a
                      href={resolveFileUrl(submission.id_card_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: theme.colors.primary }}
                    >
                      View ID Card
                    </a>
                  </MetaValue>
                </MetaItem>
              </>
            )}
            {supportingDocs.cvUrl && (
              <>
                <MetaItem>
                  <MetaLabel theme={theme}>Resume / CV</MetaLabel>
                  <MetaValue theme={theme}>
                    <a
                      href={supportingDocs.cvUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: theme.colors.primary }}
                    >
                      Download uploaded CV
                    </a>
                  </MetaValue>
                </MetaItem>
              </>
            )}
            {Object.keys(registrationFiles).length > 0 && (
              <MetaItem>
                <MetaLabel theme={theme}>Other Attachments</MetaLabel>
                <MetaValue theme={theme}>
                  <ChipsRow>
                    {Object.entries(registrationFiles).map(([key, url]) => (
                      <Chip key={key} theme={theme}>
                        <File size={14} />
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {key.replace(/_/g, ' ')}
                        </a>
                      </Chip>
                    ))}
                  </ChipsRow>
                </MetaValue>
              </MetaItem>
            )}
            {!submission.id_card_path && !supportingDocs.cvUrl && Object.keys(registrationFiles).length === 0 && (
              <MetaItem>
                <MetaLabel theme={theme}>Documents</MetaLabel>
                <MetaValue theme={theme} style={{ color: theme.colors.text.muted }}>
                  No documents uploaded
                </MetaValue>
              </MetaItem>
            )}
          </MetaRow>
        </Card>
      </TwoColumn>

      {/* Card: Application details & accessibility */}
      <Card>
        <SectionTitle theme={theme}>
          <Calendar size={18} />
          Application Details
        </SectionTitle>
        {accessibility.disabilityStatus !== null ||
        (accessibility.disabilityTypes && accessibility.disabilityTypes.length) ? (
          <>
            <SectionTitle theme={theme} style={{ marginTop: '1rem' }}>
              Accessibility Information
            </SectionTitle>
            <FieldGrid>
              <FieldItem theme={theme}>
                <FieldLabel theme={theme}>Identifies as having a physical disability</FieldLabel>
                <FieldValue theme={theme}>
                  {accessibility.disabilityStatus === null
                    ? 'Not provided'
                    : accessibility.disabilityStatus
                      ? 'Yes'
                      : 'No'}
                </FieldValue>
              </FieldItem>
              {accessibility.disabilityStatus && (
                <FieldItem theme={theme}>
                  <FieldLabel theme={theme}>Disability Type(s)</FieldLabel>
                  <FieldValue theme={theme}>
                    {accessibility.disabilityTypes && accessibility.disabilityTypes.length
                      ? accessibility.disabilityTypes.join(', ')
                      : 'Not specified'}
                  </FieldValue>
                </FieldItem>
              )}
              {accessibility.disabilityStatus && accessibility.disabilityOther && (
                <FieldItem theme={theme}>
                  <FieldLabel theme={theme}>Additional Details</FieldLabel>
                  <FieldValue theme={theme}>{accessibility.disabilityOther}</FieldValue>
                </FieldItem>
              )}
            </FieldGrid>
          </>
        ) : (
          <EmptyState theme={theme} style={{ marginTop: '1rem' }}>
            No accessibility details were provided in this application.
          </EmptyState>
        )}
      </Card>

      {/* Card: Training interest & history */}
      <Card>
        <SectionTitle theme={theme}>
          <Calendar size={18} />
          Training Interests & History
        </SectionTitle>

        {interestedTrainings && interestedTrainings.length > 0 && (
          <>
            <SectionTitle theme={theme} style={{ marginTop: '1rem' }}>
              Interested Trainings
            </SectionTitle>
            <ChipsRow>
              {interestedTrainings.map((training, idx) => (
                <Chip key={idx} theme={theme}>
                  {training}
                </Chip>
              ))}
            </ChipsRow>
          </>
        )}

        <SectionTitle theme={theme} style={{ marginTop: '1.5rem' }}>
          Previous Training History
        </SectionTitle>
        <FieldGrid>
          <FieldItem theme={theme}>
            <FieldLabel theme={theme}>Has Previous Training</FieldLabel>
            <FieldValue theme={theme}>{hasPreviousTraining ? 'Yes' : 'No'}</FieldValue>
          </FieldItem>
        </FieldGrid>
        {hasPreviousTraining && previousTrainings && previousTrainings.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            {previousTrainings.map((training, idx) => (
              <div
                key={idx}
                style={{
                  background: theme.colors.background.secondary,
                  padding: '1rem',
                  borderRadius: theme.borderRadius.md,
                  marginBottom: '1rem',
                  borderLeft: `4px solid ${theme.colors.primary}`,
                }}
              >
                <h4 style={{ margin: '0 0 0.75rem 0', color: theme.colors.text.primary }}>
                  Training #{idx + 1}
                </h4>
                <FieldGrid>
                  <FieldItem theme={theme}>
                    <FieldLabel theme={theme}>Training Name</FieldLabel>
                    <FieldValue theme={theme}>{training.name || 'N/A'}</FieldValue>
                  </FieldItem>
                  <FieldItem theme={theme}>
                    <FieldLabel theme={theme}>Trainer / Institution</FieldLabel>
                    <FieldValue theme={theme}>{training.institution || 'N/A'}</FieldValue>
                  </FieldItem>
                  <FieldItem theme={theme}>
                    <FieldLabel theme={theme}>Training Date</FieldLabel>
                    <FieldValue theme={theme}>
                      {training.date ? new Date(training.date).toLocaleDateString() : 'N/A'}
                    </FieldValue>
                  </FieldItem>
                  {training.certificate_path && (
                    <FieldItem theme={theme}>
                      <FieldLabel theme={theme}>Certificate</FieldLabel>
                      <FieldValue theme={theme}>
                        <a
                          href={resolveFileUrl(training.certificate_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: theme.colors.primary }}
                        >
                          View Certificate
                        </a>
                      </FieldValue>
                    </FieldItem>
                  )}
                </FieldGrid>
              </div>
            ))}
          </div>
        )}
        {hasPreviousTraining && (!previousTrainings || previousTrainings.length === 0) && (
          <p style={{ color: theme.colors.text.muted, marginTop: '0.5rem' }}>
            No training details provided.
          </p>
        )}
        {!hasPreviousTraining && (!interestedTrainings || interestedTrainings.length === 0) && (
          <EmptyState theme={theme} style={{ marginTop: '1rem' }}>
            No training information was provided in this application.
          </EmptyState>
        )}
      </Card>

      {/* Card: Full form responses in form order */}
      <Card>
        <SectionTitle theme={theme}>
          <FileText size={18} />
          Full Form Responses
        </SectionTitle>
        {loadingForm && <p>Loading form definition…</p>}
        {!loadingForm && applicationFields.length === 0 && (
          <EmptyState theme={theme}>
            No form definition found for this application. Showing raw data is not available.
          </EmptyState>
        )}
        {!loadingForm && applicationFields.length > 0 && (
          <FormRows>
            {applicationFields.map((field) => {
              const key = field.field_name;
              const label =
                field.field_label ||
                key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

              return (
                <FormRow key={key} theme={theme}>
                  <FormRowLabel theme={theme}>{label}</FormRowLabel>
                  <FormRowValue theme={theme}>{renderFieldValue(field)}</FormRowValue>
                </FormRow>
              );
            })}
          </FormRows>
        )}
      </Card>
    </PageContainer>
  );
};

export default MemberApplicationView;


